import { 
  collection, 
  query, 
  where, 
  orderBy, 
  limit, 
  onSnapshot, 
  doc, 
  updateDoc, 
  addDoc, 

  writeBatch,
  Timestamp,
  startAfter,
  getDocs
} from 'firebase/firestore';
import { getToken, onMessage, type Messaging, type MessagePayload } from 'firebase/messaging';
import { db, initializeMessaging } from '@/core/config/firebase';
import { authStore } from '@/core/store/auth.store';
import { notificationsStore } from '@/core/store/notifications.store';
import type { NotificationBase, FCMToken } from '@/core/types';
import { COLECCIONES } from '@/core/constants/collections';
import { toJsDateOrNull, toMillisOr, type FirestoreDateLike } from '@shared/fechas/fecha-like';
import { getErrorMessage, getErrorCode } from '@shared/utils/errores';

/**
 * @file src/core/services/notifications.service.ts
 * @description Servicio para manejo completo de notificaciones y FCM
 */

class NotificationsService {
  private unsubscribeListener: (() => void) | null = null;
  private messaging: Messaging | null = null;
  private isInitialized = false;
  private checkInterval: number | null = null; // Intervalo para verificar notificaciones programadas

  /**
   * Inicializa el servicio de notificaciones
   */
  async init(): Promise<void> {
    if (this.isInitialized) return;

    try {
      console.log('🔔 Inicializando servicio de notificaciones...');
      
      // Configurar FCM (no crítico si falla)
      try {
        await this.initializeFCM();
      } catch (fcmError) {
        console.warn('FCM no pudo inicializarse:', fcmError);
        // Continuar sin FCM
      }
      
      // Configurar listener de notificaciones en tiempo real
      this.setupNotificationsListener();
      
      // Iniciar verificación periódica de notificaciones programadas
      this.startPeriodicCheck();
      
      // Procesar notificaciones pendientes (solo si hay datos)
      try {
        this.processUnshownNotifications();
      } catch (processError) {
        console.warn('Error procesando notificaciones pendientes:', processError);
      }
      
      this.isInitialized = true;
      console.log('🔔 Servicio de notificaciones inicializado');
    } catch (error) {
      console.error('Error inicializando servicio de notificaciones:', error);
      // Marcar como inicializado para evitar reintentos
      this.isInitialized = true;
      // Inicializar store vacío para funcionalidad básica
      notificationsStore.setNotifications([]);
    }
  }

  /**
   * Inicializa Firebase Cloud Messaging
   */
  private async initializeFCM(): Promise<void> {
    try {
      this.messaging = await initializeMessaging();
      
      if (!this.messaging) {
        console.warn('FCM no disponible en este navegador');
        return;
      }
      
      // Verificar permisos de notificación
      let permission = Notification.permission;
      
      if (permission === 'default') {
        // Solo solicitar si aún no se ha decidido
        try {
          permission = await Notification.requestPermission();
        } catch (error) {
          console.warn('No se pudo solicitar permisos de notificación:', error);
          permission = 'denied';
        }
      }
      
      if (permission !== 'granted') {
        console.warn('🔕 Notificaciones deshabilitadas. Estado:', permission);
        
        // Mostrar ayuda específica según el estado
        if (permission === 'denied') {
          this.handleDeniedPermissions();
        } else {
          console.info('ℹ️ Las notificaciones están disponibles pero no habilitadas');
        }
        
        // Continuar sin FCM pero con funcionalidad básica
        return;
      }

      // Obtener token FCM
      const token = await getToken(this.messaging, {
        vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY
      });

      if (token) {
        await this.saveTokenToFirestore(token);
        // Limpiar tokens inactivos antiguos (ejecutar en segundo plano, no bloquear)
        this.cleanupInactiveTokens().catch(err => 
          console.warn('Error limpiando tokens inactivos:', err)
        );
      }

      // Escuchar mensajes en primer plano
      onMessage(this.messaging, (payload) => {
        this.handleForegroundMessage(payload);
      });

    } catch (error) {
      // Capturar errores específicos de "push service not available" o "AbortError"
      const isAbortError = error instanceof Error && error.name === 'AbortError';
      if (isAbortError || getErrorMessage(error).includes('push service not available')) {
        console.warn('⚠️ FCM: El servicio de push no está disponible en este navegador o entorno (Localhost/HTTP). Ignorando silenciosamente.');
      } else {
        console.error('Error configurando FCM:', error);
      }
    }
  }

  /**
   * Guarda el token FCM en Firestore
   * Optimizado para evitar duplicados: verifica si el token ya existe y lo actualiza
   * en lugar de crear uno nuevo. También desactiva tokens duplicados del mismo dispositivo.
   */
  private async saveTokenToFirestore(token: string): Promise<void> {
    const currentUser = authStore.getState().user;
    if (!currentUser) return;

    try {
      const dispositivo = this.getDeviceInfo();
      const tokensRef = collection(db, COLECCIONES.FCM_TOKENS);
      
      // Buscar si ya existe este token exacto
      const existingTokenQuery = query(
        tokensRef,
        where('token', '==', token),
        where('userId', '==', currentUser.uid)
      );
      const existingTokenSnapshot = await getDocs(existingTokenQuery);

      if (!existingTokenSnapshot.empty) {
        // Token ya existe, actualizar fecha y asegurar que esté activo
        const existingDoc = existingTokenSnapshot.docs[0];
        await updateDoc(existingDoc.ref, {
          activo: true,
          dispositivo: dispositivo,
          fechaCreacion: Timestamp.now() // Actualizar fecha para mantenerlo fresco
        });
        console.log('🔄 Token FCM actualizado:', token.substring(0, 20) + '...');
        return;
      }

      // Buscar tokens del mismo dispositivo/usuario que puedan ser duplicados
      const deviceTokensQuery = query(
        tokensRef,
        where('userId', '==', currentUser.uid),
        where('dispositivo', '==', dispositivo),
        where('activo', '==', true)
      );
      const deviceTokensSnapshot = await getDocs(deviceTokensQuery);

      // Si hay tokens activos del mismo dispositivo, desactivarlos (solo mantener el nuevo)
      if (!deviceTokensSnapshot.empty) {
        const batch = writeBatch(db);
        deviceTokensSnapshot.docs.forEach(doc => {
          batch.update(doc.ref, { activo: false });
        });
        await batch.commit();
        console.log(`🔄 ${deviceTokensSnapshot.docs.length} token(s) antiguo(s) del dispositivo desactivado(s)`);
      }

      // Crear nuevo token
      const tokenData: Omit<FCMToken, 'id'> = {
        token,
        userId: currentUser.uid,
        dispositivo: dispositivo,
        fechaCreacion: Timestamp.now(),
        activo: true
      };

      await addDoc(tokensRef, tokenData);
      console.log('✅ Token FCM registrado:', token.substring(0, 20) + '...');
    } catch (error) {
      console.error('Error guardando token FCM:', error);
    }
  }

  /**
   * Configura el listener de notificaciones en tiempo real
   */
  private setupNotificationsListener(): void {
    const currentUser = authStore.getState().user;
    if (!currentUser) {
      console.warn('🔔 No hay usuario autenticado para configurar listener de notificaciones');
      return;
    }

    try {
      console.log(`🔔 Configurando listener de notificaciones para usuario: ${currentUser.uid}`);
      console.log(`🔔 Colección de notificaciones: ${COLECCIONES.NOTIFICATIONS}`);
      
      const notificationsRef = collection(db, COLECCIONES.NOTIFICATIONS);
      
      // IMPORTANTE: Mostrar todas las notificaciones (pasadas, presentes y futuras)
      // Ordenar por fechaCreacion descendente para mostrar las más recientes primero
      // No filtrar por fechaCreacion <= now() para que se muestren también las programadas
      const q = query(
        notificationsRef,
        where('userId', '==', currentUser.uid),
        orderBy('fechaCreacion', 'desc'),
        limit(100) // Aumentar límite para incluir más notificaciones
      );

      this.unsubscribeListener = onSnapshot(q, 
        (snapshot) => {
          console.log(`🔔 ${snapshot.docs.length} notificaciones encontradas`);
          
          const notifications = snapshot.docs.map(doc => {
            const data = doc.data();
            
            // Convertir fechas de Firestore a Date si es necesario
            const fechaCreacion = toJsDateOrNull(data.fechaCreacion as FirestoreDateLike) ?? data.fechaCreacion;
            const fechaProgramada = toJsDateOrNull(data.fechaProgramada as FirestoreDateLike) ?? data.fechaProgramada;
            const fechaLimite = toJsDateOrNull(data.fechaLimite as FirestoreDateLike) ?? data.fechaLimite;

            return {
              id: doc.id,
              ...data,
              fechaCreacion,
              fechaProgramada,
              fechaLimite
            };
          }) as NotificationBase[];

          // Ordenar por fechaCreacion descendente (más recientes primero)
          notifications.sort((a, b) => {
            const dateA = toMillisOr(a.fechaCreacion, 0);
            const dateB = toMillisOr(b.fechaCreacion, 0);
            return dateB - dateA;
          });

          if (notifications.length > 0) {
            const now = new Date();
            
            // Contar notificaciones que ya deberían mostrarse
            const visibles = notifications.filter(n => {
              if (n.leida) return false;
              
              // Si tiene fechaProgramada, solo contar si ya pasó
              if (n.fechaProgramada) {
                const fechaProg = toJsDateOrNull(n.fechaProgramada);
                return fechaProg ? fechaProg <= now : true;
              }

              // Sin fechaProgramada, es inmediata
              return true;
            });

            const futuras = notifications.filter(n => {
              if (!n.fechaProgramada) return false;
              const fechaProg = toJsDateOrNull(n.fechaProgramada);
              return fechaProg ? fechaProg > now : false;
            });
            
            // Logging reducido - solo información esencial
            if (notifications.length > 0) {
              console.log(`🔔 ${notifications.length} notificaciones cargadas (${visibles.length} visibles, ${futuras.length} programadas)`);
            }
          }

          notificationsStore.setNotifications(notifications);
          
          // Procesar nuevas notificaciones no mostradas (solo las que ya deberían mostrarse)
          this.processUnshownNotifications();
        },
        (error) => {
          console.error('Error en listener de notificaciones:', error);
          
          if (error.code === 'permission-denied') {
            console.warn('❌ Sin permisos para acceder a notificaciones. Verificar reglas de Firestore.');
          } else if (error.code === 'failed-precondition' && error.message.includes('index')) {
            console.warn('📊 Índice de Firestore requerido para notificaciones. Creando automáticamente...');
            console.info('💡 Los índices pueden tardar unos minutos en estar disponibles.');
            console.info('💡 Verifica en Firebase Console > Firestore > Indexes que existe un índice compuesto para:');
            console.info('   - Colección: notifications');
            console.info('   - Campos: userId (Ascending), fechaCreacion (Descending)');
          } else if (error.code === 'unavailable') {
            console.warn('🔄 Firestore temporalmente no disponible. Reintentando...');
            // Reintentar después de un delay
            setTimeout(() => {
              if (!this.isInitialized) return;
              this.setupNotificationsListener();
            }, 5000);
            return;
          } else {
            console.error('Error inesperado en notificaciones:', error);
          }
          
          // Inicializar store vacío para mantener funcionalidad básica
          notificationsStore.setNotifications([]);
        }
      );
    } catch (error) {
      console.error('Error configurando listener de notificaciones:', error);
      // Inicializar store vacío como fallback
      notificationsStore.setNotifications([]);
    }
  }

  /**
   * Procesa notificaciones que no se han mostrado como popup flotante
   * Solo muestra notificaciones que ya deberían mostrarse (fechaProgramada <= ahora)
   * IMPORTANTE: Las notificaciones con fechaProgramada en el futuro NO se muestran
   */
  private processUnshownNotifications(): void {
    const now = new Date();
    const allNotifications = notificationsStore.getState().notifications;
    
    // Filtrar notificaciones que:
    // 1. No han sido mostradas
    // 2. No han sido leídas
    // 3. Ya deberían mostrarse (fechaProgramada <= ahora, o si no hay fechaProgramada, usar fechaCreacion)
    const unshownNotifications = allNotifications.filter(n => {
      if (n.mostrada || n.leida) return false;
      
      // PRIORIDAD: Si hay fechaProgramada, SOLO mostrar si ya pasó
      if (n.fechaProgramada) {
        const fechaProgramada = toJsDateOrNull(n.fechaProgramada) ?? new Date(0);

        // Si la fechaProgramada es en el futuro, NO mostrar aún
        if (fechaProgramada > now) {
          return false;
        }

        // La fechaProgramada ya pasó, puede mostrarse
        return true;
      }

      // Si NO hay fechaProgramada, es una notificación inmediata
      // Usar fechaCreacion para determinar si mostrar
      const fecha = toJsDateOrNull(n.fechaCreacion) ?? new Date(0);

      return fecha <= now;
    });

    if (unshownNotifications.length > 0) {
      // Ordenar por fechaCreacion descendente y mostrar la más reciente
      unshownNotifications.sort((a, b) => {
        const dateA = toMillisOr(a.fechaCreacion, 0);
        const dateB = toMillisOr(b.fechaCreacion, 0);
        return dateB - dateA;
      });
      
      const latestNotification = unshownNotifications[0];
      console.log(`🔔 Mostrando notificación flotante: ${latestNotification.titulo}`);
      this.showFloatingNotification(latestNotification);
    }
  }

  /**
   * Muestra una notificación flotante
   */
  private showFloatingNotification(notification: NotificationBase): void {
    notificationsStore.showFloatingNotification(notification);
    
    // Marcar como mostrada después de un delay
    setTimeout(() => {
      this.markNotificationAsShown(notification.id);
    }, 500);
  }

  /**
   * Inicia la verificación periódica de notificaciones programadas
   * Verifica cada minuto si hay notificaciones cuya fechaProgramada ya pasó
   */
  private startPeriodicCheck(): void {
    // Limpiar intervalo anterior si existe
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
    }
    
    // Verificar cada minuto (60000 ms)
    this.checkInterval = window.setInterval(() => {
      this.checkScheduledNotifications();
    }, 60000);
  }

  /**
   * Verifica si hay notificaciones programadas que ya deberían mostrarse
   * y actualiza el estado para que la campanita aparezca automáticamente
   */
  private checkScheduledNotifications(): void {
    const now = new Date();
    const currentState = notificationsStore.getState();
    const notifications = currentState.notifications;
    
    // Verificar si hay notificaciones que ahora deberían mostrarse
    const shouldUpdate = notifications.some(n => {
      if (n.leida) return false;
      
      // Si tiene fechaProgramada, verificar si ya pasó
      if (n.fechaProgramada) {
        const fechaProgramada = toJsDateOrNull(n.fechaProgramada);
        if (!fechaProgramada) return false;

        // Si la fechaProgramada acaba de pasar (o está muy cerca), actualizar
        return fechaProgramada <= now;
      }
      
      return false;
    });
    
    // Si hay notificaciones que deberían mostrarse, forzar actualización del store
    if (shouldUpdate) {
      // Forzar recálculo del unreadCount actualizando las notificaciones
      notificationsStore.setNotifications([...notifications]);
      // Procesar notificaciones no mostradas
      this.processUnshownNotifications();
    }
  }

  /**
   * Maneja mensajes FCM recibidos en primer plano
   */
  private handleForegroundMessage(payload: MessagePayload): void {
    console.log('📩 Mensaje FCM recibido:', payload);

    // El mensaje ya debe estar en Firestore gracias a Cloud Functions
    // Solo mostramos una notificación del navegador si el usuario lo desea
    if (payload.notification) {
      this.showBrowserNotification(payload.notification);
    }
  }

  /**
   * Muestra notificación nativa del navegador
   */
  private showBrowserNotification(notification: NonNullable<MessagePayload['notification']>): void {
    if (Notification.permission === 'granted') {
      new Notification(notification.title ?? 'QuetzalApp', {
        body: notification.body,
        icon: '/logo2.svg',
        badge: '/logo2.svg'
      });
    }
  }

  /**
   * Marca una notificación como leída
   */
  async markAsRead(notificationId: string): Promise<void> {
    try {
      const notificationRef = doc(db, COLECCIONES.NOTIFICATIONS, notificationId);
      await updateDoc(notificationRef, {
        leida: true,
        fechaLectura: Timestamp.now()
      });

      notificationsStore.markAsRead(notificationId);
    } catch (error) {
      console.error('Error marcando notificación como leída:', error);
    }
  }

  /**
   * Marca una notificación como mostrada
   */
  async markNotificationAsShown(notificationId: string): Promise<void> {
    try {
      const notificationRef = doc(db, COLECCIONES.NOTIFICATIONS, notificationId);
      await updateDoc(notificationRef, {
        mostrada: true,
        fechaMostrada: Timestamp.now()
      });

      notificationsStore.markAsShown(notificationId);
    } catch (error) {
      console.error('Error marcando notificación como mostrada:', error);
    }
  }

  /**
   * Oculta la campanita flotante
   */
  hideFloatingNotification(): void {
    notificationsStore.hideFloatingNotification();
  }

  /**
   * Carga más notificaciones (paginación)
   */
  async loadMoreNotifications(lastNotification?: NotificationBase): Promise<NotificationBase[]> {
    const currentUser = authStore.getState().user;
    if (!currentUser) return [];

    try {
      const notificationsRef = collection(db, COLECCIONES.NOTIFICATIONS);
      let q = query(
        notificationsRef,
        where('userId', '==', currentUser.uid),
        orderBy('fechaCreacion', 'desc'),
        limit(10)
      );

      if (lastNotification) {
        q = query(q, startAfter(lastNotification.fechaCreacion));
      }

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as NotificationBase[];

    } catch (error) {
      console.error('Error cargando más notificaciones:', error);

      if (getErrorCode(error) === 'failed-precondition' && getErrorMessage(error).includes('index')) {
        console.warn('📊 Esperando disponibilidad de índices de Firestore...');
      }

      return [];
    }
  }

  /**
   * Actualiza el estado de completado de una tarea
   */
  updateTaskCompletion(tareaId: string, completada: boolean): void {
    notificationsStore.updateTaskCompletion(tareaId, completada);
  }



  /**
   * Maneja el caso cuando los permisos están denegados
   */
  private handleDeniedPermissions(): void {
    console.group('🔕 Guía para habilitar notificaciones');
    console.info('Las notificaciones están bloqueadas para este sitio.');
    console.info('Para habilitarlas:');
    console.info('1. Haz clic en el ícono de candado 🔒 junto a la URL');
    console.info('2. Busca "Notificaciones" y cambia a "Permitir"');
    console.info('3. Recarga la página');
    console.groupEnd();
    
    // Opcionalmente mostrar un toast o modal informativo
    if (window.location.hostname !== 'localhost') {
      this.showNotificationHelp();
    }
  }

  /**
   * Muestra ayuda visual para habilitar notificaciones
   */
  private showNotificationHelp(): void {
    // Crear un toast informativo no intrusivo
    const toast = document.createElement('div');
    toast.className = 'fixed top-4 right-4 z-50 bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-sm shadow-lg';
    toast.innerHTML = `
      <div class="flex items-start space-x-3">
        <div class="bg-blue-100 rounded-full p-2">
          <i class="fas fa-bell-slash text-blue-600 text-sm"></i>
        </div>
        <div class="flex-1">
          <h4 class="text-sm font-semibold text-blue-900 mb-1">Notificaciones deshabilitadas</h4>
          <p class="text-xs text-blue-700 mb-2">Para recibir alertas importantes, habilita las notificaciones en la configuración del navegador.</p>
          <button onclick="this.parentElement.parentElement.parentElement.remove()" class="text-xs text-blue-600 hover:text-blue-800 underline">
            Entendido
          </button>
        </div>
        <button onclick="this.parentElement.parentElement.remove()" class="text-blue-400 hover:text-blue-600">
          <i class="fas fa-times text-xs"></i>
        </button>
      </div>
    `;
    
    document.body.appendChild(toast);
    
    // Auto-remover después de 10 segundos
    setTimeout(() => {
      if (toast.parentElement) {
        toast.remove();
      }
    }, 10000);
  }

  /**
   * Obtiene información del dispositivo
   */
  private getDeviceInfo(): string {
    const userAgent = navigator.userAgent;
    if (userAgent.includes('Chrome')) return 'Chrome';
    if (userAgent.includes('Firefox')) return 'Firefox';
    if (userAgent.includes('Safari')) return 'Safari';
    if (userAgent.includes('Edge')) return 'Edge';
    return 'Desconocido';
  }

  /**
   * Limpia tokens FCM inactivos antiguos del usuario actual
   * Elimina tokens inactivos con más de 30 días de antigüedad para reducir costos
   */
  private async cleanupInactiveTokens(): Promise<void> {
    const currentUser = authStore.getState().user;
    if (!currentUser) return;

    try {
      const tokensRef = collection(db, COLECCIONES.FCM_TOKENS);
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const thirtyDaysAgoTimestamp = Timestamp.fromDate(thirtyDaysAgo);

      // Buscar tokens inactivos antiguos del usuario
      const inactiveTokensQuery = query(
        tokensRef,
        where('userId', '==', currentUser.uid),
        where('activo', '==', false),
        where('fechaCreacion', '<', thirtyDaysAgoTimestamp)
      );

      const inactiveTokensSnapshot = await getDocs(inactiveTokensQuery);

      if (!inactiveTokensSnapshot.empty) {
        const batch = writeBatch(db);
        let deletedCount = 0;

        // Limitar a 100 tokens por batch para evitar exceder el límite
        inactiveTokensSnapshot.docs.slice(0, 100).forEach(doc => {
          batch.delete(doc.ref);
          deletedCount++;
        });

        await batch.commit();
        console.log(`🧹 Limpiados ${deletedCount} token(s) FCM inactivo(s) antiguo(s)`);
      }
    } catch (error) {
      // No lanzar error, solo loguear (es una operación de limpieza no crítica)
      console.warn('Advertencia al limpiar tokens inactivos:', error);
    }
  }

  /**
   * Verifica el estado actual de los permisos de notificación
   */
  getNotificationPermissionStatus(): { 
    permission: NotificationPermission; 
    isAvailable: boolean; 
    canRequest: boolean; 
  } {
    const permission = Notification.permission;
    const isAvailable = 'Notification' in window;
    const canRequest = permission === 'default';
    
    return { permission, isAvailable, canRequest };
  }

  /**
   * Intenta solicitar permisos de notificación nuevamente
   */
  async requestNotificationPermission(): Promise<boolean> {
    if (!('Notification' in window)) {
      console.warn('Notificaciones no soportadas en este navegador');
      return false;
    }

    if (Notification.permission === 'granted') {
      console.info('✅ Los permisos ya están concedidos');
      return true;
    }

    if (Notification.permission === 'denied') {
      console.warn('❌ Los permisos están denegados. Debe habilitarlos manualmente en la configuración del navegador.');
      this.handleDeniedPermissions();
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        console.info('✅ Permisos de notificación concedidos');
        // Reinicializar FCM si es necesario
        if (!this.messaging) {
          await this.initializeFCM();
        }
        return true;
      } else {
        console.warn('❌ Permisos de notificación denegados por el usuario');
        return false;
      }
    } catch (error) {
      console.error('Error solicitando permisos de notificación:', error);
      return false;
    }
  }

  /**
   * Limpia el servicio (usado en logout)
   */
  destroy(): void {
    if (this.unsubscribeListener) {
      this.unsubscribeListener();
      this.unsubscribeListener = null;
    }
    
    // Limpiar intervalo de verificación periódica
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
    
    notificationsStore.clearState();
    this.isInitialized = false;
  }
}

export const notificationsService = new NotificationsService();